import styled from '@emotion/styled'
import { ReactNode } from 'react'

import { Profession, Specialization } from '@bemlo/enums'
import { ShiftTimes } from '@bemlo/schedule'

import { OverviewCalendarRow } from './overview-calendar/OverviewCalendarRow'
import { OverviewCalendarTenderTitle } from './overview-calendar/OverviewCalendarTenderTitle'
import { useGetHoursPerWeek } from './overview-calendar/useGetHoursPerWeek'
import { Color, theme } from '../../constants'
import {
  Avatar,
  Clickable,
  Div,
  EducationBadge,
  Flex,
  Text,
} from '../../elements'

type Tender = {
  id: string
  title: string
  profession: Profession
  specializations: Specialization[]
  startDate: number
  endDate: number
  vacantShifts: ShiftTimes[]
}

type Booking = {
  id: string
  name: string
  avatarUrl?: string
  profession: Profession
  specializations: Specialization[]
  tenderTitle: string
  startDate: number
  endDate: number
  bookedShifts: ShiftTimes[]
}

type UnitOverviewCalendarProps = {
  startDate: number
  endDate: number
  tenders: Tender[]
  bookings: Booking[]
  controls?: ReactNode
  onClickBooking: (id: string) => void
  onClickTender: (id: string) => void
}

const Title = styled(Clickable)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  padding: `${theme.spacing[1]}px ${theme.spacing[2]}px`,
  borderRadius: theme.border.radius.small,
  ':hover': {
    backgroundColor: Color.LIGHT_GREY,
  },
})

export const UnitOverviewCalendar = ({
  startDate,
  endDate,
  tenders,
  bookings,
  controls = null,
  onClickBooking,
  onClickTender,
}: UnitOverviewCalendarProps) => {
  const [weeks, getHoursPerWeek] = useGetHoursPerWeek(startDate, endDate)

  return (
    <Flex col styles={{ width: '100%', background: Color.WHITE }}>
      <OverviewCalendarRow
        type="HEADER"
        title={controls}
        weeks={weeks.map((week) => week.number)}
      />
      {tenders.map((tender) => (
        <OverviewCalendarRow
          key={tender.id}
          type="TENDER"
          title={
            <OverviewCalendarTenderTitle onClick={onClickTender} {...tender} />
          }
          weeks={getHoursPerWeek(tender.vacantShifts, tender)}
        />
      ))}
      {bookings.map((booking) => (
        <OverviewCalendarRow
          key={booking.id}
          type="BOOKING"
          title={
            <Title onClick={() => onClickBooking(booking.id)}>
              <Flex gap={2.5} styles={{ alignItems: 'center', flex: 1 }}>
                <Avatar
                  size="sm"
                  type={booking.avatarUrl ? 'image' : 'initials'}
                  name={booking.name}
                  image={booking.avatarUrl}
                />
                <Flex col gap={0.5}>
                  <Text bold fontSize={theme.font.size[6]}>
                    {booking.name}
                  </Text>
                  <Text color={Color.DARKER_GREY} fontSize={theme.font.size[6]}>
                    {booking.tenderTitle}
                  </Text>
                </Flex>
              </Flex>
              <Div styles={{ flex: 1 }}>
                <EducationBadge
                  profession={booking.profession}
                  specializations={booking.specializations}
                />
              </Div>
            </Title>
          }
          weeks={getHoursPerWeek(booking.bookedShifts, booking)}
        />
      ))}
    </Flex>
  )
}
